import { setTokenCookie } from '@root/utils/cookies/cookies';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { authSlice, resetAuthData, selectAuthWithEmail, selectTokenPair } from '../../slices/authSlice';
import { getAuthenticationIntentPath, setAuthenticationIntent } from './authenticationIntent';
import { DefaultLoader } from './DefaultLoader';

interface WithNoAuthenticationProps {
  replacePath?: string;
}

export const withNoAuthentication =
  ({ replacePath = '/workspace' }: WithNoAuthenticationProps = {}) =>
  (Component) =>
  (props) => {
    const router = useRouter();
    const {
      appauth,
      redirect_to_desktop: redirectToDesktop,
      documentSlug,
      backToEmbed,
      t: queryToken,
      openCommentBubble,
      resetToken
    } = router.query;

    const [loadedOnClient, setLoadedOnClient] = useState(false);
    const { token, refresh } = useSelector(selectTokenPair);

    const [isHandlingAuthentication, setIsHandlingAuthentication] = useState(false);

    const dispatch = useDispatch();
    const { authenticatedByEmailRedirectUrl } = useSelector(selectAuthWithEmail);

    const handleUnauthenticated = () => {
      setLoadedOnClient(true);
    };

    const handleAuthenticated = () => {
      setIsHandlingAuthentication(true);
      setTokenCookie({ access: token, refresh: queryToken ?? refresh, preventOverwrite: true });
      if (backToEmbed === 'true') {
        window.close();
        return;
      }

      // if replacePath is '/welcome', always go to welcome (for signup flows that need onboarding)
      // otherwise, check for authentication intent first
      if (replacePath !== '/welcome') {
        const authIntentPath = getAuthenticationIntentPath();

        if (authIntentPath) {
          router.push(authIntentPath);
          return;
        }
      }

      if (authenticatedByEmailRedirectUrl) {
        const url = authenticatedByEmailRedirectUrl;

        router
          .push(url)
          .then(() => {
            dispatch(authSlice.actions.setAuthenticatedRedirectUrl({ url: null }));
          })
          .finally(() => {
            setIsHandlingAuthentication(false);
          });
      } else if (redirectToDesktop === 'true') {
        router.push(`/open-with-desktop?t=${refresh}`).finally(() => {
          setIsHandlingAuthentication(false);
        });
      } else {
        router.push(replacePath + (window && window.location.search ? window.location.search : '')).finally(() => {
          setIsHandlingAuthentication(false);
        });
      }
    };

    const checkAuthenticated = async () => {
      // Handle resetToken parameter
      if (resetToken === 'true') {
        dispatch(resetAuthData());

        // Remove resetToken from URL and continue
        const newQuery = { ...router.query };

        delete newQuery.resetToken;

        router.replace(
          {
            pathname: router.pathname,
            query: newQuery
          },
          undefined,
          { shallow: true }
        );

        handleUnauthenticated();

        return;
      }

      if (appauth === 'true') {
        setAuthenticationIntent('/approve-device', 10 * 60); // 10 minutes
      }
      if (documentSlug) {
        let documentSlugWithQueryParams = documentSlug;

        if (openCommentBubble) {
          documentSlugWithQueryParams += `${documentSlugWithQueryParams.includes('?') ? '&' : '?'}openCommentBubble=${openCommentBubble}`;
        }
        if (window.location.hash) {
          documentSlugWithQueryParams += `${window.location.hash}`;
        }
        setAuthenticationIntent(documentSlugWithQueryParams, 10 * 60); // 10 minutes
      }
      if (token) {
        handleAuthenticated();
      } else {
        handleUnauthenticated();
      }
    };

    useEffect(() => {
      if (window.localStorage.getItem('userDeleted')) {
        handleUnauthenticated();
        return;
      }
      checkAuthenticated();
      // Intentially not including checkAuthenticated in the dependency array
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return loadedOnClient ? <Component {...{ ...props, isHandlingAuthentication }} /> : <DefaultLoader />;
  };
